<script setup lang="ts">
import { Authenticator } from '@aws-amplify/ui-vue'
import NotificationsSlideover from '~/components/NotificationsSlideover.vue';
import NotificationBell from '~/components/NotificationBell.vue';
import type { QuickSightDashboard, MenuLink } from '~/types';
import { useAlertStore } from '../stores/alert';
import { I18n } from 'aws-amplify/utils'
import { translations } from '@aws-amplify/ui-vue'

I18n.putVocabularies(translations)
I18n.setLanguage('en')

I18n.putVocabulariesForLanguage('en', {
  'Setup TOTP': 'Configure your two-factor authentication',
  'Confirm TOTP Code': 'Please enter your 6-digit code',
  'Enter your code': 'Enter the 6-digit code',
  'Enter your confirmation code': 'Enter the 6-digit code sent to you',
  'Back to Sign In': 'Return',
  'Confirm': 'Submit Code',
})


const route = useRoute();
const auth = useAuthStore();
const notificationStore = useNotificationStore();
const globalStore = useGlobalStore();
const { isNotificationsSlideoverOpen } = useDashboard()
const config = useRuntimeConfig();
const alertStore = useAlertStore();
const dashboardLinks = ref([]);
const links = ref<MenuLink[]>([]);
const sideBarKey = ref(0);
const maintenanceKey = ref(0);
const maintenanceModeEnabled = ref<boolean>(false);

useHead({
  titleTemplate: (titleChunk) => {
    return titleChunk ? `${titleChunk} - Workforce Development Institute` : 'Workforce Development Institute'
  }
})

callOnce(() => {
  auth.init();
});

const authFormfields = {
  setupTotp: {
    QR: {
      totpIssuer: config.public.environment === 'prod' ? 'WDIPortal' : `WDIPortal-${config.public.environment}`,
    },
  },
}

async function fetchAvailableDashboards() {
  dashboardLinks.value = [];
  if (auth.isAuthenticated) {
    const { data: availableDashboards } = await useApi<QuickSightDashboard[]>(`/v1/csp/quicksightdashboards`);
    availableDashboards.value.forEach((dashboard: QuickSightDashboard) => {
      const item = {
        id: dashboard.reportId,
        label: dashboard.displayName,
        to: `/report/${dashboard.reportId}/`,
      };
      dashboardLinks.value.push(item);
    });
    const reportsIdx = links.value.findIndex(obj => obj.id=="reports");
    if (reportsIdx !== -1) {
      links.value[reportsIdx].children = [...dashboardLinks.value];
    }
    visibleLinks = getVisibleLinks();
    sideBarKey.value += 1;
  }
}

async function initMenu() {
 links.value = [{
  id: 'home',
  label: 'Dashboard',
  icon: 'i-heroicons-home',
  to: '/',
  tooltip: {
    text: 'Dashboard',
    shortcuts: ['G', 'H']
  }, 
  isVisible: true
}, {
  id: 'cases',
  label: 'Cases',
  icon: 'i-heroicons-queue-list', 
  isVisible: true,
  children: [{
    label: 'New Case',
    to: '/new-case',
    exact: true,
    tooltip: {
      text: 'New Case',
      shortcuts: ['G', 'N']
    }
  }, {
    label: 'Search Case',
    to: '/search',
    tooltip: {
      text: 'Search Case',
      shortcuts: ['G', 'S']
    }
  }]
},
{
  id: 'childcareproviders',
  label: 'Child Care Providers',
  icon: 'i-heroicons-building-office-2',
  to: '/providerlist',
  tooltip: {
    text: 'Child Care Providers',
    shortcuts: ['C', 'P']
  }, 
  isVisible: true
},
{
  id: 'reports',
  label: 'Reports',
  icon: 'i-heroicons-chart-bar-square',
  isVisible: await auth.getHasQuicksightAccess() || await auth.getIsCspAdmin(),
  children: []
},
{
  id: 'childcaresubs',
  label: 'CSP Admin',
  icon: 'i-heroicons-face-smile',
  defaultOpen: route.path.startsWith('/csp-admin'),
  isVisible: await auth.getIsCspAdmin(),
  children: [
  {
      id: 'users',
      label: 'Users',
      to: '/csp-admin/users',
      tooltip: {
        text: 'CSP User Management',
      },
      isVisible: !await auth.getIsSystemAdmin(),
  },  
  {
    id: 'assignmentattributes',
    label: 'Case Assignments',
    to: '/csp-admin/case-assignments',
    tooltip: {
      text: 'Case Assignments'
    }
  },
  {
    id: 'inocomeeligibility',
    label: 'Income Eligibility',
    icon: 'i-heroicons-clipboard-document-check',
    to: '/csp-admin/income-eligibility',
    tooltip: {
      text: 'Income Eligibility'
     }
  },
  {
    id: 'reports',
    label: 'Reports',
    to: '/csp-admin/reports',
    tooltip: {
      text: 'Reports'
    }
  },
  {
    id: 'scholarship-age-dashboard',
    label: 'Scholarship Age Alerts',
    to: '/csp-admin/scholarship-age-dashboard',
    tooltip: { text: 'Scholarship Age Alerts' }
  },
  {
    id: 'scholarship-enrollment-dashboard',
    label: 'Scholarship Enrollment',
    to: '/csp-admin/scholarship-enrollment-dashboard',
    tooltip: { text: 'Scholarship Enrollment' }
  },
  {
    id: 'scholarship-end-date-dashboard',
    label: 'Renewals Dashboard',
    to: '/csp-admin/scholarship-end-date-dashboard',
    tooltip: { text: 'Renewals Dashboard' }
  }],
  tooltip: {
    text: 'Child Care Subsidy Program Admins'
  }
},
{
  id: 'admin',
  label: 'System Admin',
  icon: 'i-heroicons-cog-8-tooth',
  defaultOpen: route.path.startsWith('/admin'),
  isVisible: await auth.getIsSystemAdmin() || await auth.getIsCspAdmin(),
  children: [{
    id: 'users',
    label: 'Users',
    to: '/admin/users',
    tooltip: {
        text: 'User Management',
    },
    isVisible: await auth.getIsSystemAdmin()
  },
  {
    id: 'manage-flags',
    label: 'Manage Flags ',
    to: '/admin/manage-flags',
    tooltip: {
      text: 'Manage Admin Flags'
    }
  }]
}]
}

const groups = [{
  key: 'links',
  label: 'Go to',
  commands: links.value.map(link => ({ ...link, shortcuts: link.tooltip?.shortcuts }))
}]

let visibleLinks = getVisibleLinks();

//
function getVisibleLinks() {
  return links.value
    .filter(link => link.isVisible !== false)
    .map(link => {
      if (link.children) {
        return {
          ...link,
          children: link.children.filter(child => child.isVisible !== false)
        };
      }
      return link;
    });
}

watch(() => auth.isAuthenticated, async (newVal) => {
  if (newVal) {
    await initMenu();
    visibleLinks = getVisibleLinks();
    sideBarKey.value += 1;
    notificationStore.startPolling();

    maintenanceKey.value += 1;
  }
});

watch(() => globalStore.isMaintenanceModeEnabled, async (newVal) => {
  maintenanceModeEnabled.value = !!newVal;
  maintenanceKey.value += 1;
});

onMounted(async () => {
  await initMenu();
  visibleLinks = getVisibleLinks();
  sideBarKey.value += 1;
  if (auth.isAuthenticated) {
    notificationStore.startPolling();
    maintenanceKey.value += 1;
  }
})

</script>

<template>
  <Authenticator 
    :login-mechanisms="['email']" 
    :hide-sign-up="true" 
    :form-fields="authFormfields"
    >
    <template #header>
      <div class="p5 mt-10">
        <img src="/wdi-logo-rgb.png" alt="WDI Logo" class="w-1/2 m-auto mb-10">
      </div>
    </template>
    <UDashboardLayout v-if="auth.isAuthenticated && (!maintenanceModeEnabled || auth.isSystemAdmin)">
      <UDashboardPanel :width="250" :resizable="{ min: 200, max: 300 }" collapsible>
        <UDashboardNavbar class="!border-transparent" :ui="{ left: 'flex-1' }">
          <template #left>
            <img v-if="$colorMode.value == 'dark'" src="/wdi-logo.png" alt="WDI Logo" class="w-3/4 m-auto">
            <img v-else src="/wdi-logo-rgb.png" alt="WDI Logo" class="w-3/4 m-auto">
          </template>
          <template #right>
            <UTooltip text="Notifications" :shortcuts="['N']">
              <UButton color="gray" variant="ghost" square @click="isNotificationsSlideoverOpen = true">
                <NotificationBell />
              </UButton>
            </UTooltip>
          </template>
        </UDashboardNavbar>

      <UDashboardSidebar v-if="auth.isAuthenticated">
        <template #header>
          <div class="flex-1 mt-3" />
          <CspCaseQuickSearch />
        </template>

        <!-- <UDivider class="sticky bottom-0" /> -->
        <UDashboardSidebarLinks 
          :key="sideBarKey" 
          :links="visibleLinks" 
          @click="(event) => {
              if ((event.target as HTMLSpanElement).textContent?.trim().toLowerCase() === 'reports') {
                fetchAvailableDashboards();
              }
            }" />
          <div class="flex-1" />
          <TeamsDropdown />

          <template #footer>
            <!-- ~/components/UserDropdown.vue -->
            <UserDropdown />
          </template>
        </UDashboardSidebar>
      </UDashboardPanel>
        <slot />

        <!-- ~/components/HelpSlideover.vue -->
        <HelpSlideover />
        <!-- ~/components/NotificationsSlideover.vue -->
        <NotificationsSlideover  />

        <ClientOnly>
          <LazyUDashboardSearch :groups="groups" />
        </ClientOnly>
    </UDashboardLayout>
    <UProgress v-else animation="carousel" />
    <MaintenanceModeMessage v-if="maintenanceModeEnabled && !auth.isSystemAdmin" :key="maintenanceKey"  />  
    <UAlert
      v-if="alertStore.showAlert"
      :title="alertStore.alertMessage"
      :class="`u-alert--${alertStore.alertType}`"
      @close="alertStore.clear"
    />
    <UAlert
      v-if="auth.isSystemAdmin && maintenanceModeEnabled" 
      :key="maintenanceKey"
      title="The site is in maintenance mode, please note changes may not be persisted."
      class="u-alert--error"
      icon="i-heroicons-wrench-screwdriver"
    />    
  </Authenticator>
</template>
<style scoped>
.u-alert--info {
  background-color: #fff8e1;
  border-left: 4px solid #ffca28;
  color: #6f4e37; 
}
.u-alert--error {
  background-color: #ffebee;
  border-left: 4px solid #c62828;
  color: #b71c1c;
}
.u-alert--success {
  background-color: #e8f5e9;
  border-left: 4px solid #2e7d32;
  color: #1b5e20;
}
</style>